/**
 * Seletor dos modelos de bloco de oferta do Botão de Ação.
 *
 * A grade sai inteira de `ACTION_BUTTON_MODELS` e a miniatura de cada card é
 * desenhada a partir do `layout` do próprio spec — se um modelo ganhar ou perder
 * um slot amanhã, a miniatura acompanha sozinha, sem edição aqui.
 */

import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ActionButtonModel } from '@/store/videoFeatures';
import {
  ACTION_BUTTON_MODELS,
  type ActionButtonModelSpec,
  type ActionButtonSlot,
} from '@/lib/action-button';

interface ActionButtonModelSelectorProps {
  selectedModel: ActionButtonModel;
  onSelect: (model: ActionButtonModel) => void;
}

/**
 * Traço de cada slot na miniatura. `buttons` é o único que precisa do spec
 * inteiro, porque a forma dos botões depende de `secondaryAs`/`buttonsInline`.
 */
const SLOT_SKETCH: Partial<Record<ActionButtonSlot, () => React.ReactNode>> = {
  urgency: () => <div className="h-[5px] w-2/3 rounded-full bg-[#DC2626]/70" />,
  progressBar: () => (
    <div className="h-[6px] w-full rounded-full bg-slate-200">
      <div className="h-full w-3/4 rounded-full bg-[#DC2626]/70" />
    </div>
  ),
  headline: () => <div className="h-[6px] w-4/5 rounded-full bg-slate-700" />,
  subheadline: () => <div className="h-[4px] w-3/5 rounded-full bg-slate-400" />,
  offerText: () => <div className="h-[4px] w-2/3 rounded-full bg-slate-400" />,
  strikethrough: () => (
    <div className="relative h-[5px] w-1/3 rounded-full bg-slate-300">
      <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-slate-500" />
    </div>
  ),
  featured: () => <div className="h-[8px] w-1/2 rounded-full bg-[#0D9488]" />,
  price: () => <div className="h-[9px] w-1/2 rounded-full bg-[#1D4FFF]" />,
  priceNote: () => <div className="h-[3px] w-1/4 rounded-full bg-slate-300" />,
};

/** Bloco de botões da miniatura: primário + segundo caminho, se houver. */
const ButtonsSketch: React.FC<{ spec: ActionButtonModelSpec }> = ({ spec }) => {
  const primary = <div className="h-[11px] flex-1 rounded-[3px] bg-[#1D4FFF]" />;

  if (!spec.secondaryAs) {
    return <div className="flex w-3/4 justify-center">{primary}</div>;
  }

  if (spec.secondaryAs === 'link') {
    return (
      <div className="flex w-3/4 flex-col items-center gap-[4px]">
        <div className="flex w-full">{primary}</div>
        <div className="h-[3px] w-2/3 rounded-full bg-slate-300" />
      </div>
    );
  }

  const secondaryButton = (
    <div className="h-[11px] flex-1 rounded-[3px] border border-slate-300 bg-slate-100" />
  );

  return (
    <div
      className={cn(
        'flex w-3/4 gap-[4px]',
        spec.buttonsInline ? 'flex-row' : 'flex-col',
      )}
    >
      <div className="flex w-full">{primary}</div>
      <div className="flex w-full">{secondaryButton}</div>
    </div>
  );
};

/** Miniatura completa: percorre o layout de cima para baixo. */
const ModelSketch: React.FC<{ spec: ActionButtonModelSpec }> = ({ spec }) => (
  <div className="flex h-[76px] w-full flex-col items-center justify-center gap-[4px] overflow-hidden rounded-lg border border-slate-200 bg-white px-3 py-2">
    {spec.layout.map((slot) => {
      if (slot === 'buttons') return <ButtonsSketch key={slot} spec={spec} />;

      const sketch = SLOT_SKETCH[slot];
      if (!sketch) return null;

      return (
        <div key={slot} className="flex w-full justify-center">
          {sketch()}
        </div>
      );
    })}
  </div>
);

export const ActionButtonModelSelector: React.FC<ActionButtonModelSelectorProps> = ({
  selectedModel,
  onSelect,
}) => {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {ACTION_BUTTON_MODELS.map((spec) => {
        const isActive = spec.id === selectedModel;

        return (
          <button
            key={spec.id}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onSelect(spec.id);
            }}
            className={cn(
              'group relative flex flex-col items-center gap-2 rounded-xl border p-2 text-center transition-colors',
              isActive
                ? 'border-[#4B7BFF] bg-[#F0F5FF]'
                : 'border-slate-200 bg-white hover:border-[#4B7BFF]/30',
            )}
          >
            {isActive && (
              <span className="absolute right-2 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-[#4B7BFF] text-white">
                <Check className="h-3 w-3" strokeWidth={3} />
              </span>
            )}

            <ModelSketch spec={spec} />

            <span
              className={cn(
                'text-[11px] font-medium transition-colors',
                isActive ? 'text-[#4B7BFF]' : 'text-slate-600 group-hover:text-[#4B7BFF]',
              )}
            >
              {spec.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};
